import React, { useState, useEffect, useRef } from 'react';
import {
  FaCopy, FaDownload, FaFilePdf, FaRegStopCircle,
  FaListAlt, FaClock, FaFileContract
} from "react-icons/fa";
import { LuSpeech } from "react-icons/lu";
import ReactMarkdown from "react-markdown";
import remarkGFM from "remark-gfm";
import html2pdf from "html2pdf.js";
import { toast } from "react-toastify";

const SummaryContent = ({ summaries, onClose }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [isSpeaking, setIsSpeaking] = useState(false);

  const synthRef = useRef(window.speechSynthesis);
  const utteranceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (synthRef.current.speaking) {
        synthRef.current.cancel();
      }
    };
  }, []);

  const cancelSpeech = () => {
    if (synthRef.current.speaking) {
      synthRef.current.cancel();
    }
    setIsSpeaking(false);
  };

  const getContentKey = (tab) => {
    const keyMap = {
      summary: 'contract_summary',
      timeline: 'contract_timelines',
      term: 'contract_terms_conditions',
    };
    return keyMap[tab] || 'contract_summary';
  };

  const getCurrentContent = () => {
    if (!summaries) return null;
    const key = getContentKey(activeTab);
    return summaries[key];
  };

  const getPlainText = () => {
    const content = getCurrentContent();
    if (!content) return '';
    if (Array.isArray(content)) {
      return content.map(item => `${item.date || ''}: ${item.event || ''}`).join("\n");
    }
    return String(content).replace(/[*_#`]+/g, "").trim();
  };

  const handleCopy = async () => {
    const text = getPlainText();
    if (!text) return toast.info("No content available to copy");
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied to clipboard!");
    } catch (err) {
      toast.error("Failed to copy text");
    }
  };

  const handleDownload = async () => {
    const content = getCurrentContent();
    const textPlain = getPlainText();
    
    if (!content) return toast.error("No content to download");

    try {
      const title = activeTab.charAt(0).toUpperCase() + activeTab.slice(1);
      const element = document.createElement("div");
      element.style.padding = "20px";
      element.style.fontFamily = "Arial, sans-serif";
      
      let htmlBody = Array.isArray(content) 
        ? content.map(i => `<p><strong>${i.date}</strong>: ${i.event}</p>`).join("")
        : textPlain.replace(/\n/g, "<br/>");

      element.innerHTML = `
        <h2 style="border-bottom: 2px solid #333; padding-bottom: 10px;">Contract ${title}</h2>
        <div style="margin-top: 15px; font-size: 12pt; color: #333; line-height: 1.6;">${htmlBody}</div>
      `;

      const opt = {
        margin: 15,
        filename: `Contract_${title}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      };
      await html2pdf().from(element).set(opt).save();
      toast.success("Download started");
    } catch (error) {
      console.error(error);
      toast.error("Download failed");
    }
  };

  const handleSpeechToggle = () => {
    const text = getPlainText();
    if (!text) return toast.info("No content to read");

    if (isSpeaking) {
      cancelSpeech();
      return;
    }

    cancelSpeech();
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    setIsSpeaking(true);
    synthRef.current.speak(utterance);
  };

  const renderContent = () => {
    const content = getCurrentContent();
    if (!content) return <div className="text-center text-muted mt-5">No content available.</div>;

    if (activeTab === 'timeline' && Array.isArray(content)) {
      return (
        <div className="timeline-container">
          {content.map((item, index) => (
            <div key={index} className="timeline-item">
              <div className="timeline-dot"></div>
              <div className="timeline-content">
                <div className="timeline-date">{item.date}</div>
                <div className="timeline-event">{item.event}</div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="markdown-body">
        <ReactMarkdown remarkPlugins={[remarkGFM]}>{String(content)}</ReactMarkdown>
      </div>
    );
  };

  const footerBtnStyle = {
    backgroundColor: '#fff',
    border: '1px solid #dee2e6',
    color: '#495057',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '6px',
    transition: 'all 0.2s'
  };

  return (
    <div className="d-flex flex-column h-100">
      <style>{`
        /* Timeline Styles */
        .timeline-container {
          position: relative;
          padding-left: 30px;
        }
        
        .timeline-item {
          position: relative;
        }
        
        .timeline-item:last-child {
          padding-bottom: 0;
        }
        
        .timeline-item::before {
          content: '';
          position: absolute;
          left: -19px;
          top: 20px;
          bottom: -30px;
          width: 2px;
          background-color: #6c63ff;
        }
        
        .timeline-item:last-child::before {
          display: none;
        }
        
        .timeline-dot {
          position: absolute;
          left: -25px;
          top: 10px;
          width: 14px;
          height: 14px;
          background-color: #6c63ff;
          border-radius: 50%;
          border: 3px solid #fff;
          box-shadow: 0 0 0 2px #6c63ff;
        }
        
        .timeline-content {
          padding-left: 10px;
        }
        
        .timeline-date {
          color: #6c63ff;
          font-weight: 600;
          font-size: 14px;
          margin-bottom: 4px;
        }
        
        .timeline-event {
          color: #000;
          font-weight: 600;
          font-size: 15px;
          line-height: 1.4;
        }
        
        /* Markdown Content Styles */
        .markdown-body {
          padding: 0;
          color: #333;
          line-height: 1.6;
        }
        
        .markdown-body p {
          margin-bottom: 12px;
          color: #555;
          font-size: 14px;
        }
        
        .markdown-body h1,
        .markdown-body h2,
        .markdown-body h3 {
          font-weight: 700;
          color: #000;
          margin-top: 16px;
          margin-bottom: 12px;
        }
        
        .markdown-body h1 {
          font-size: 18px;
        }
        
        .markdown-body h2 {
          font-size: 16px;
        }
        
        .markdown-body h3 {
          font-size: 15px;
        }
        
        .markdown-body ul {
          margin-left: 0;
          padding-left: 20px;
          margin-bottom: 16px;
        }
        
        .markdown-body ul ul {
          margin-left: 0;
          padding-left: 24px;
          margin-top: 8px;
          margin-bottom: 8px;
        }
        
        .markdown-body li {
          margin-bottom: 8px;
          color: #333;
          font-size: 14px;
        }
        
        .markdown-body li > p {
          margin-bottom: 4px;
        }
        
        .markdown-body strong {
          font-weight: 700;
          color: #000;
        }
        
        .markdown-body code {
          background-color: #f5f5f5;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 13px;
        }
        
        /* Other Styles */
        .nav-tabs .nav-link { border: none; color: #6c757d; font-weight: 500; padding: 12px 20px; }
        .nav-tabs .nav-link.active { color: #0d6efd; border-bottom: 2px solid #0d6efd; background: transparent; }
        .footer-btn:hover { background-color: #f8f9fa; border-color: #c1c9d0; }
      `}</style>

      {/* Tabs */}
      <div className="border-bottom px-3">
        <ul className="nav nav-tabs card-header-tabs">
          {['summary', 'timeline', 'term'].map((tab) => (
            <li className="nav-item" key={tab}>
              <button 
                className={`nav-link ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'summary' && <FaListAlt className="me-2"/>}
                {tab === 'timeline' && <FaClock className="me-2"/>}
                {tab === 'term' && <FaFileContract className="me-2"/>}
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Scrollable Content */}
      <div className="flex-grow-1 p-4" style={{ overflowY: 'auto', minHeight: '300px', maxHeight: '60vh', backgroundColor: '#fff' }}>
        {renderContent()}
      </div>

      {/* Footer */}
      <div className="p-3 border-top bg-white d-flex justify-content-between align-items-center">
        <div className="d-flex gap-2">
          <button className="btn footer-btn" style={footerBtnStyle} onClick={handleCopy}>
            <FaCopy /> Copy
          </button>
          <button className="btn footer-btn" style={footerBtnStyle} onClick={handleDownload}>
            <FaFilePdf className="text-danger"/> PDF
          </button>
          <button className="btn footer-btn" style={footerBtnStyle} onClick={handleSpeechToggle}>
             {isSpeaking ? <FaRegStopCircle className="text-danger"/> : <LuSpeech className="text-primary"/>} 
             {isSpeaking ? "Stop" : "Read"}
          </button>
        </div>

        <div>
          <button className="btn btn-secondary" style={{ padding: '8px 24px', fontWeight: '500' }} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default SummaryContent;